import React, { useState, useEffect } from "react";
import { db } from "../../firebase";
import {
  collection, getDocs, doc, updateDoc, serverTimestamp,
} from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import {
  Key, Plus, Copy, CheckCircle, Loader2,
  BookOpen, BarChart2, Code, AlertTriangle,
} from "lucide-react";

function ApiAccessPage({ companyId }) {
  const [apiKeys,      setApiKeys]      = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [generating,   setGenerating]   = useState(false);
  const [newKeyName,   setNewKeyName]   = useState("");
  const [revealedKey,  setRevealedKey]  = useState(null);
  const [error,        setError]        = useState("");
  const [success,      setSuccess]      = useState("");

  useEffect(() => {
    if (!companyId) return;
    loadApiKeys();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  async function loadApiKeys() {
    setLoading(true);
    try {
      const snap = await getDocs(
        collection(db, "companies", companyId, "api_keys")
      );
      setApiKeys(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (err) {
      setError(`Failed to load: ${err.message} (${err.code})`);
    } finally {
      setLoading(false);
    }
  }

  async function handleGenerateKey() {
    if (!newKeyName.trim()) return setError("Enter a name for this API key.");
    setGenerating(true);
    setError("");
    try {
      const functions    = getFunctions(undefined, "europe-west1");
      const generateKey  = httpsCallable(functions, "generateApiKey");
      const result       = await generateKey({ companyId, keyName: newKeyName.trim() });
      setRevealedKey(result.data.apiKey);
      setNewKeyName("");
      await loadApiKeys();
      setSuccess("API key generated. Copy it now — it will not be shown again.");
      setTimeout(() => setSuccess(""), 10000);
    } catch (err) {
      setError(`Failed to generate: ${err.message} (${err.code})`);
    } finally {
      setGenerating(false);
    }
  }

  async function handleRevokeKey(keyId) {
    if (!window.confirm("Revoke this API key? Any system using it will lose access immediately.")) return;
    setError("");
    try {
      await updateDoc(
        doc(db, "companies", companyId, "api_keys", keyId),
        { active: false, revoked_at: serverTimestamp() }
      );
      await loadApiKeys();
      setSuccess("API key revoked.");
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      setError(`Failed to revoke: ${err.message} (${err.code})`);
    }
  }

  const BASE_URL = "https://europe-west1-pinpoint-e6d4c.cloudfunctions.net/api";

  const ENDPOINTS = [
    {
      method: "GET",
      path:   "/clients",
      desc:   "Get all clients in your company",
      params: "?limit=100&page=1&section=zone_id",
    },
    {
      method: "GET",
      path:   "/clients/:id",
      desc:   "Get a single client by ID",
      params: "",
    },
    {
      method: "POST",
      path:   "/clients",
      desc:   "Create a new client",
      params: "",
      body:   '{ "first_name": "Ahmed", "last_name": "Ali", "phone": "0501234567", "lat": 25.2, "lng": 55.3 }',
    },
    {
      method: "GET",
      path:   "/events",
      desc:   "Get recent events (visits, alerts)",
      params: "?type=checkin&limit=50&from=2024-01-01",
    },
    {
      method: "GET",
      path:   "/vendors",
      desc:   "Get all active vendors",
      params: "",
    },
    {
      method: "GET",
      path:   "/stats",
      desc:   "Get company statistics",
      params: "",
    },
  ];

  const activeKeyCount  = apiKeys.filter(k => k.active !== false).length;
  const totalRequests   = apiKeys.reduce((sum, k) => sum + (k.request_count || 0), 0);
  const lastUsedKey     = apiKeys
    .filter(k => k.last_used_at)
    .sort((a, b) => (b.last_used_at?.seconds || 0) - (a.last_used_at?.seconds || 0))[0];

  return (
    <div className="space-y-6">

      {/* Header */}
      <div className="bg-white rounded-xl shadow-sm p-5">
        <div className="flex items-center gap-3">
          <Code className="w-5 h-5 text-yellow-500" />
          <div>
            <h2 className="text-lg font-semibold text-gray-800">API Access</h2>
            <p className="text-gray-500 text-sm mt-0.5">
              Integrate Pinpoint with your external systems using REST API keys.
            </p>
          </div>
        </div>
      </div>

      {/* Alerts */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 text-red-500 shrink-0 mt-0.5" />
          <p className="text-red-700 text-sm">{error}</p>
          <button onClick={() => setError("")} className="ml-auto text-red-400 hover:text-red-600 text-xs underline shrink-0">Dismiss</button>
        </div>
      )}
      {success && (
        <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-center gap-2">
          <CheckCircle className="w-4 h-4 text-green-600 shrink-0" />
          <p className="text-green-700 text-sm font-medium">{success}</p>
        </div>
      )}

      {/* ── Section 1: API Keys ──────────────────────────────────────────── */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <h3 className="text-sm font-semibold text-gray-700 mb-4 pb-2 border-b flex items-center gap-2">
          <Key className="w-4 h-4" /> API Keys
        </h3>

        {/* Generate form */}
        <div className="flex gap-3 mb-6">
          <input
            value={newKeyName}
            onChange={e => setNewKeyName(e.target.value)}
            onKeyDown={e => e.key === "Enter" && handleGenerateKey()}
            placeholder="Key name (e.g. ERP Integration, Mobile App)"
            className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400"
          />
          <button
            onClick={handleGenerateKey}
            disabled={generating || !newKeyName.trim()}
            className="bg-yellow-500 hover:bg-yellow-600 text-white text-sm font-medium px-4 py-2 rounded-lg disabled:opacity-50 flex items-center gap-1.5 shrink-0">
            {generating
              ? <><Loader2 className="w-4 h-4 animate-spin" /> Generating...</>
              : <><Plus className="w-4 h-4" /> Generate Key</>
            }
          </button>
        </div>

        {/* Revealed key — shown once after generation */}
        {revealedKey && (
          <div className="bg-green-50 border border-green-300 rounded-xl p-4 mb-4">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle className="w-4 h-4 text-green-600 shrink-0" />
              <p className="text-sm font-semibold text-green-800">
                Copy your API key now — it will not be shown again
              </p>
            </div>
            <div className="flex items-center gap-2">
              <code className="flex-1 bg-white border border-green-200 rounded-lg px-3 py-2 text-sm font-mono text-gray-800 break-all">
                {revealedKey}
              </code>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(revealedKey);
                  setSuccess("Copied to clipboard!");
                  setTimeout(() => setSuccess(""), 2000);
                }}
                className="bg-green-500 hover:bg-green-600 text-white text-sm px-3 py-2 rounded-lg flex items-center gap-1.5 shrink-0">
                <Copy className="w-4 h-4" /> Copy
              </button>
            </div>
            <button
              onClick={() => setRevealedKey(null)}
              className="text-xs text-green-600 mt-2 underline">
              I have copied the key — hide it
            </button>
          </div>
        )}

        {/* Keys list */}
        {loading ? (
          <div className="flex items-center gap-2 text-gray-400 text-sm py-4">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading...
          </div>
        ) : apiKeys.length === 0 ? (
          <p className="text-gray-400 text-sm text-center py-6">
            No API keys yet. Generate one to get started.
          </p>
        ) : (
          <div className="space-y-3">
            {apiKeys.map(key => (
              <div key={key.id}
                className={`flex items-center justify-between p-3 rounded-lg border ${
                  key.active !== false
                    ? "border-gray-200 bg-gray-50"
                    : "border-red-100 bg-red-50"
                }`}>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-800 truncate">{key.name}</p>
                  <div className="flex items-center gap-3 mt-0.5 flex-wrap">
                    <p className="text-xs text-gray-400 font-mono">
                      {key.key_prefix}••••••••
                    </p>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                      key.active !== false
                        ? "bg-green-100 text-green-700"
                        : "bg-red-100 text-red-700"
                    }`}>
                      {key.active !== false ? "Active" : "Revoked"}
                    </span>
                    {(key.request_count || 0) > 0 && (
                      <span className="text-xs text-blue-600 font-medium">
                        {key.request_count.toLocaleString()} req
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-400 mt-0.5">
                    Created:{" "}
                    {key.created_at?.toDate
                      ? key.created_at.toDate().toLocaleDateString()
                      : "—"}
                    {key.last_used_at && (
                      <> · Last used: {key.last_used_at.toDate().toLocaleDateString()}</>
                    )}
                  </p>
                </div>
                {key.active !== false && (
                  <button
                    onClick={() => handleRevokeKey(key.id)}
                    className="text-xs text-red-600 hover:text-red-800 bg-red-50 hover:bg-red-100 px-3 py-1.5 rounded-lg shrink-0 ml-3">
                    Revoke
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Section 2: API Documentation ────────────────────────────────── */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <h3 className="text-sm font-semibold text-gray-700 mb-4 pb-2 border-b flex items-center gap-2">
          <BookOpen className="w-4 h-4" /> API Documentation
        </h3>

        <div className="space-y-5 text-sm">

          <div>
            <p className="font-medium text-gray-700 mb-1">Base URL</p>
            <code className="block bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-xs font-mono text-gray-800 break-all">
              {BASE_URL}
            </code>
          </div>

          <div>
            <p className="font-medium text-gray-700 mb-1">Authentication</p>
            <p className="text-gray-500 text-xs mb-2">
              Include your API key in every request header:
            </p>
            <code className="block bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-xs font-mono text-gray-800">
              X-API-Key: your_api_key_here
            </code>
          </div>

          <div>
            <p className="font-medium text-gray-700 mb-3">Endpoints</p>
            <div className="space-y-2">
              {ENDPOINTS.map((endpoint, i) => (
                <div key={i} className="border border-gray-200 rounded-lg overflow-hidden">
                  <div className="flex items-center gap-3 px-3 py-2 bg-gray-50">
                    <span className={`text-xs font-bold px-2 py-0.5 rounded font-mono ${
                      endpoint.method === "GET"
                        ? "bg-green-100 text-green-700"
                        : "bg-blue-100 text-blue-700"
                    }`}>
                      {endpoint.method}
                    </span>
                    <code className="text-xs font-mono text-gray-800">
                      {endpoint.path}{endpoint.params}
                    </code>
                  </div>
                  <div className="px-3 py-2">
                    <p className="text-xs text-gray-500">{endpoint.desc}</p>
                    {endpoint.body && (
                      <code className="block mt-1 bg-gray-50 rounded px-2 py-1 text-xs font-mono text-gray-600 break-all">
                        {endpoint.body}
                      </code>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div>
            <p className="font-medium text-gray-700 mb-1">Example Request</p>
            <code className="block bg-gray-900 text-green-400 rounded-lg px-4 py-3 text-xs font-mono whitespace-pre-wrap">
{`curl -X GET "${BASE_URL}/clients?limit=50" \\
  -H "X-API-Key: pk_xxxxxxxx_..."`}
            </code>
          </div>

        </div>
      </div>

      {/* ── Section 3: Usage Stats ───────────────────────────────────────── */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <h3 className="text-sm font-semibold text-gray-700 mb-4 pb-2 border-b flex items-center gap-2">
          <BarChart2 className="w-4 h-4" /> API Usage
        </h3>
        <div className="grid grid-cols-3 gap-4">
          <div className="text-center">
            <p className="text-2xl font-bold text-blue-600">{activeKeyCount}</p>
            <p className="text-xs text-gray-500 mt-0.5">Active Keys</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-green-600">{totalRequests.toLocaleString()}</p>
            <p className="text-xs text-gray-500 mt-0.5">Total Requests</p>
          </div>
          <div className="text-center">
            <p className="text-lg font-bold text-gray-700">
              {lastUsedKey
                ? lastUsedKey.last_used_at.toDate().toLocaleDateString()
                : "Never"
              }
            </p>
            <p className="text-xs text-gray-500 mt-0.5">Last Request</p>
          </div>
        </div>
      </div>

    </div>
  );
}

export default ApiAccessPage;
